import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { isEmpty } from 'underscore';
import Api from '../api/Api';
import { IWmsProps } from '../components/features/Map/Layer/DatasetMesh';
import { QUERY_KEYS } from './queryKeys';

export const useMetadataPreview = (datasetId: string) => {
  const enabled = !isEmpty(datasetId);

  return useQuery({
    queryKey: [QUERY_KEYS.dataset.metadataPreview, datasetId],
    queryFn: () => Api.metadata.getMetadataPreviewText(datasetId),
    enabled,
    gcTime: 1000 * 60 * 15,
  });
};

export const useDataPreview = (text?: string) => {
  const enabled = !!text;

  return useQuery({
    queryKey: [QUERY_KEYS.dataset.dataPreview, text],
    queryFn: () => Api.dataPreview.getDataPreview(text),
    enabled,
    gcTime: 1000 * 60 * 15,
  });
};

export const useMeshLayerQuery = (datasetId: string, showMesh: boolean) => {
  const metadataQuery = useMetadataPreview(datasetId);
  const dataPreviewQuery = useDataPreview(metadataQuery.data);
  const [meshUrl, setMeshUrl] = useState<IWmsProps>();
  const [meshResults, setMeshResults] = useState();

  useEffect(() => {
    if (!showMesh) {
      setMeshUrl(undefined);
      return;
    }
    const data = dataPreviewQuery.data;
    if (!data || !data.obj) return;

    try {
      const parsedObject = JSON.parse(data.obj);
      if (parsedObject && parsedObject.p_wms.p_wmsGeoOption) {
        const options = parsedObject.p_wms.p_wmsGeoOption;
        const layer = options.layers;
        const style = options.styles;
        const date = options.sourceDateTime;

        if (layer && style && date) {
          setMeshUrl({
            layers: layer,
            styles: style,
            sourceItem: '1',
            sourceDateTime: date,
            width: 256,
            height: 256,
          } as IWmsProps);
        }
      }
    } catch (error) {
      console.error('Error parsing data preview:', error);
    }
  }, [dataPreviewQuery.data, setMeshUrl, showMesh]);

  return {
    meshUrl: meshUrl,
    setMeshUrl: setMeshUrl,
    meshResults: meshResults,
    setMeshResults: setMeshResults,
    metadataData: metadataQuery.data,
    previewData: dataPreviewQuery.data,
    isLoading: metadataQuery.isLoading || dataPreviewQuery.isLoading,
    isError: metadataQuery.isError || dataPreviewQuery.isError,
    error: metadataQuery.error || dataPreviewQuery.error,
  };
};
